import { AUDIO_ERROR_CODES } from '@shared/constants/error-codes';
import { CHAT_DICTATION_MAX_DURATION_SECONDS } from '@shared/schemas/chat-dictation';
import { extractChatErrorCode, getChatErrorMessage } from './chat-errors';

export const DICTATION_MESSAGES = {
  empty: 'Aucun son n’a été enregistré. Réessayez.',
  failed: 'La transcription a échoué. Réessayez.',
  insecure: 'La dictée vocale nécessite une connexion sécurisée (HTTPS).',
  micBusy: 'Le micro est utilisé par une autre application.',
  micDisconnected: 'Le micro a été déconnecté pendant l’enregistrement.',
  micMissing: 'Aucun micro n’a été détecté sur cet appareil.',
  micRefused: 'L’accès au micro a été refusé. Autorisez-le dans les réglages du navigateur.',
  noSpeech: 'Aucune parole n’a été détectée.',
  unsupported: 'La dictée vocale n’est pas prise en charge par ce navigateur.',
} as const;

const DICTATION_ERROR_MESSAGES: Record<string, string> = {
  [AUDIO_ERROR_CODES.AUDIO_ATTACHMENT_TOO_LARGE]: 'L’enregistrement est trop volumineux.',
  [AUDIO_ERROR_CODES.AUDIO_ATTACHMENT_TOO_LONG]:
    `La dictée est limitée à ${CHAT_DICTATION_MAX_DURATION_SECONDS / 60} minutes.`,
  [AUDIO_ERROR_CODES.AUDIO_ATTACHMENT_UNSUPPORTED]: 'L’enregistrement n’a pas pu être lu. Réessayez.',
  [AUDIO_ERROR_CODES.AUDIO_QUOTA_EXCEEDED]: 'Une transcription est déjà en cours. Réessayez dans quelques secondes.',
  [AUDIO_ERROR_CODES.AUDIO_SERVICE_UNAVAILABLE]: 'La dictée vocale est temporairement indisponible.',
  [AUDIO_ERROR_CODES.AUDIO_TRANSCRIPTION_DISABLED]: 'La dictée vocale n’est pas activée sur cet environnement.',
  [AUDIO_ERROR_CODES.AUDIO_TRANSCRIPTION_FAILED]: DICTATION_MESSAGES.failed,
  [AUDIO_ERROR_CODES.AUDIO_UPLOAD_VALIDATION_FAILED]: 'L’enregistrement n’a pas pu être lu. Réessayez.',
};

/** Server-side failure of POST /api/chat/dictation → French message, never a raw backend text. */
export function getDictationErrorMessage(error: unknown): string {
  const code = extractChatErrorCode(error);
  if (code && code in DICTATION_ERROR_MESSAGES) {
    return DICTATION_ERROR_MESSAGES[code]!;
  }
  // Chat-wide codes (quota, session) keep their existing wording.
  return code ? getChatErrorMessage(error) : DICTATION_MESSAGES.failed;
}

/** getUserMedia failure → French message. */
export function getMicrophoneErrorMessage(error: unknown): string {
  const name = error instanceof Error || error instanceof DOMException ? error.name : '';
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return DICTATION_MESSAGES.micRefused;
    case 'NotFoundError':
    case 'OverconstrainedError':
      return DICTATION_MESSAGES.micMissing;
    case 'NotReadableError':
    case 'AbortError':
      return DICTATION_MESSAGES.micBusy;
    default:
      return DICTATION_MESSAGES.unsupported;
  }
}
